<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  ACTIVE_GAMES_HUB_ALL_EMAIL_ID,
  ACTIVE_GAMES_HUB_ALL_BRANDS_ID,
  ACTIVE_GAMES_HUB_BRANDS,
  ACTIVE_GAMES_HUB_EMAIL_INBOX_IDS,
  ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
  getActiveGamesHubBrandFromQuery,
  isActiveGamesHubEmailScope,
} from 'dashboard/config/activeGamesHub';

const route = useRoute();
const router = useRouter();
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);

const selectedBrandId = computed(() => {
  if (isActiveGamesHubEmailScope(route.query.channel)) {
    return ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID;
  }
  return (
    getActiveGamesHubBrandFromQuery(route.query.brand)?.id ||
    ACTIVE_GAMES_HUB_ALL_BRANDS_ID
  );
});

const connectedBrands = computed(() =>
  ACTIVE_GAMES_HUB_BRANDS.filter(brand => brand.enabled)
);
const futureBrands = computed(() =>
  ACTIVE_GAMES_HUB_BRANDS.filter(brand => !brand.enabled)
);

const unreadCountForBrand = brand =>
  brand.inboxIds.reduce(
    (total, inboxId) => total + (getInboxUnreadCount.value(inboxId) || 0),
    0
  );

const emailUnreadCount = computed(() =>
  ACTIVE_GAMES_HUB_EMAIL_INBOX_IDS.reduce(
    (total, inboxId) => total + (getInboxUnreadCount.value(inboxId) || 0),
    0
  )
);

const selectBrand = brandId => {
  const query = { ...route.query };
  delete query.channel;
  delete query.mailbox;
  if (brandId === ACTIVE_GAMES_HUB_ALL_BRANDS_ID) {
    delete query.brand;
  } else {
    query.brand = brandId;
  }

  router.replace({
    name: 'home',
    params: { accountId: route.params.accountId },
    query,
  });
};

const selectEmail = () => {
  const query = {
    ...route.query,
    channel: ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
    mailbox: ACTIVE_GAMES_HUB_ALL_EMAIL_ID,
  };
  delete query.brand;
  router.replace({
    name: 'home',
    params: { accountId: route.params.accountId },
    query,
  });
};
</script>

<template>
  <aside
    class="hidden lg:flex h-full w-[92px] min-w-[92px] flex-col items-center border-r border-n-weak bg-n-surface-2 px-2 py-3"
    :aria-label="$t('ACTIVE_GAMES_HUB.BRAND_RAIL_LABEL')"
  >
    <div
      class="mb-3 flex size-12 items-center justify-center rounded-2xl bg-n-brand text-sm font-black tracking-tight text-white shadow-sm"
      aria-hidden="true"
    >
      {{ $t('ACTIVE_GAMES_HUB.PRODUCT_MARK') }}
    </div>

    <nav class="flex min-h-0 w-full flex-1 flex-col gap-1.5 overflow-y-auto">
      <button
        type="button"
        class="group relative flex min-h-16 w-full flex-col items-center justify-center gap-1 rounded-2xl px-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="{
          'bg-n-brand/10 text-n-brand ring-2 ring-inset ring-n-brand/40':
            selectedBrandId === ACTIVE_GAMES_HUB_ALL_BRANDS_ID,
        }"
        :aria-pressed="selectedBrandId === ACTIVE_GAMES_HUB_ALL_BRANDS_ID"
        @click="selectBrand(ACTIVE_GAMES_HUB_ALL_BRANDS_ID)"
      >
        <Icon icon="i-lucide-inbox" class="size-6" />
        <span class="text-[10px] font-bold leading-tight">
          {{ $t('ACTIVE_GAMES_HUB.ALL') }}
        </span>
      </button>

      <button
        v-for="brand in connectedBrands"
        :key="brand.id"
        v-tooltip="
          brand.enabled
            ? brand.name
            : $t('ACTIVE_GAMES_HUB.NOT_CONNECTED', { brand: brand.name })
        "
        type="button"
        class="group relative flex min-h-[76px] w-full flex-col items-center justify-center gap-1.5 rounded-2xl px-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="[
          selectedBrandId === brand.id
            ? brand.activeClass
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
          { 'cursor-not-allowed opacity-45 grayscale': !brand.enabled },
        ]"
        :disabled="!brand.enabled"
        :aria-pressed="selectedBrandId === brand.id"
        @click="selectBrand(brand.id)"
      >
        <span
          class="flex size-10 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-sm ring-1 ring-n-weak"
        >
          <img
            v-if="brand.logo"
            :src="brand.logo"
            :alt="brand.name"
            class="max-h-full max-w-full object-contain"
          />
          <span
            v-else
            class="text-[10px] font-black tracking-tighter text-n-slate-10"
          >
            {{ brand.monogram }}
          </span>
        </span>
        <span class="max-w-full truncate text-[10px] font-bold leading-tight">
          {{ brand.shortName }}
        </span>
        <span
          v-if="brand.enabled && unreadCountForBrand(brand)"
          class="absolute right-1.5 top-1.5 flex min-w-5 items-center justify-center rounded-full bg-n-ruby-9 px-1 text-[10px] font-black leading-5 text-white"
        >
          {{ unreadCountForBrand(brand) }}
        </span>
      </button>

      <div class="my-1 border-t border-n-weak" />
      <span
        class="px-1 text-center text-[9px] font-black uppercase tracking-widest text-n-slate-9"
      >
        {{ $t('ACTIVE_GAMES_HUB.CHANNELS') }}
      </span>
      <button
        type="button"
        class="group relative flex min-h-16 w-full flex-col items-center justify-center gap-1 rounded-2xl px-1 text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="{
          'bg-n-iris-2 text-n-iris-12 ring-2 ring-inset ring-n-iris-9':
            selectedBrandId === ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID,
        }"
        :aria-pressed="selectedBrandId === ACTIVE_GAMES_HUB_EMAIL_SCOPE_ID"
        @click="selectEmail"
      >
        <Icon icon="i-lucide-mail" class="size-6" />
        <span class="text-[10px] font-bold leading-tight">
          {{ $t('ACTIVE_GAMES_HUB.EMAIL') }}
        </span>
        <span
          v-if="emailUnreadCount"
          class="absolute right-1.5 top-1.5 flex min-w-5 items-center justify-center rounded-full bg-n-ruby-9 px-1 text-[10px] font-black leading-5 text-white"
        >
          {{ emailUnreadCount }}
        </span>
      </button>

      <div class="my-1 border-t border-n-weak" />
      <span
        class="px-1 text-center text-[9px] font-black uppercase tracking-widest text-n-slate-9"
      >
        {{ $t('ACTIVE_GAMES_HUB.FUTURE') }}
      </span>
      <button
        v-for="brand in futureBrands"
        :key="brand.id"
        v-tooltip="$t('ACTIVE_GAMES_HUB.NOT_CONNECTED', { brand: brand.name })"
        type="button"
        class="group relative flex min-h-[68px] w-full cursor-not-allowed flex-col items-center justify-center gap-1 rounded-2xl px-1 text-n-slate-10 opacity-45 grayscale"
        disabled
      >
        <span
          class="flex size-9 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-sm ring-1 ring-n-weak"
        >
          <span class="text-[10px] font-black tracking-tighter text-n-slate-10">
            {{ brand.monogram }}
          </span>
        </span>
        <span class="max-w-full truncate text-[10px] font-bold leading-tight">
          {{ brand.shortName }}
        </span>
      </button>
    </nav>

    <div
      class="mt-2 flex items-center gap-1 rounded-full bg-n-teal-3 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-n-teal-11"
    >
      <span class="size-1.5 rounded-full bg-n-teal-9" />
      {{ $t('ACTIVE_GAMES_HUB.LIVE') }}
    </div>
  </aside>
</template>
